@let isDesktop = deviceService.isDesktop$ | async;

<div class="min-h-screen bg-[var(--tui-background-base-alt)] text-[var(--tui-text-primary)]">
  <header
    class="sticky top-0 z-10 border-b border-[var(--tui-border-normal)] bg-[var(--tui-background-base)]/95 px-4 py-3 backdrop-blur md:px-8"
  >
    <div class="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2">
      <div class="w-full min-w-0 md:w-auto md:flex-1">
        <p
          class="text-xs uppercase tracking-[0.18em] text-[var(--tui-text-tertiary)]"
        >
          {{ 'request_form.restricted_view.guest_view' | translate }}
        </p>
        <h1 class="truncate text-xl font-semibold md:text-2xl">
          @if ($viewState() === 'success') {
            {{ 'request_form.restricted_view.request' | translate }}
            {{ $request()?.requestNumber || $requestNumber() }}
          }
        </h1>
      </div>

      <div class="flex w-full items-center gap-3 md:w-auto">
        @if ($request()?.status; as status) {
          <span
            class="rounded-full px-3 py-1 text-sm font-medium"
            [style.background]="status.color || 'var(--tui-background-accent-1)'"
            [style.color]="'var(--tui-text-primary)'"
          >
            {{ status.name }}
          </span>
        }

        <x-theme-selector
          [size]="isDesktop ? 'm' : 's'"
          class="ml-auto md:ml-0"
        />
        @if ($showLogout()) {
          <button
            type="button"
            class="rounded-xl border border-[var(--tui-border-normal)] px-4 py-2 text-sm font-medium hover:bg-[var(--tui-background-base-alt)]"
            (click)="logout()"
          >
            {{ 'request_form.restricted_view.logout' | translate }}
          </button>
        }
      </div>
    </div>
  </header>

  @if ($request(); as request) {
    <main
      class="mx-auto grid max-w-7xl gap-4 px-4 py-6 md:grid-cols-[1.6fr_1fr] md:px-8"
    >
      <section class="space-y-4">
        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.common_info' | translate }}
          </h2>
          <dl class="grid gap-3 md:grid-cols-2">
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.project_number' | translate }}
              </dt>
              <dd class="font-medium">{{ request.projectNumber || '-' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.contract' | translate }}
              </dt>
              <dd class="font-medium">{{ request.contract || '-' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.release' | translate }}
              </dt>
              <dd class="font-medium">
                {{ request.releaseDate | date: 'dd.MM.yyyy' }}
                {{ request.releaseTime }}
                {{ request.timezone }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.counterparty' | translate }}
              </dt>
              <dd class="font-medium">{{ request.counterpartyName || '-' }}</dd>
            </div>
          </dl>
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.vehicle' | translate }}
          </h2>
          <dl class="grid gap-3 md:grid-cols-2">
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.car' | translate }}
              </dt>
              <dd class="font-medium">{{ request.car || '-' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.vin' | translate }}
              </dt>
              <dd class="font-medium">{{ request.vin || '-' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{
                  'request_form.restricted_view.car_plant_number' | translate
                }}
              </dt>
              <dd class="font-medium">{{ request.carPlantNumber || '-' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.car_type' | translate }}
              </dt>
              <dd class="font-medium">{{ request.carType || '-' }}</dd>
            </div>
            <div>
              <dt class="text-sm text-[var(--tui-text-tertiary)]">
                {{ 'request_form.restricted_view.car_year' | translate }}
              </dt>
              <dd class="font-medium">{{ request.carYear || '-' }}</dd>
            </div>
          </dl>
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.products' | translate }}
          </h2>
          @if (request.products.length) {
            <ul class="space-y-2">
              @for (product of request.products; track product.id) {
                <li class="rounded-2xl bg-[var(--tui-background-base-alt)] px-4 py-3">
                  {{ getProductName(product) || '-' }}
                </li>
              }
            </ul>
          } @else {
            <p class="text-[var(--tui-text-tertiary)]">
              {{ 'request_form.restricted_view.no_products' | translate }}
            </p>
          }
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.devices' | translate }}
          </h2>
          @if (request.devices.length) {
            <ul class="space-y-3">
              @for (device of request.devices; track device.id) {
                <li
                  class="rounded-2xl bg-[var(--tui-background-base-alt)] px-4 py-3 space-y-2"
                >
                  <div class="font-medium">
                    {{ getDeviceModelName(device.deviceModelId) }}
                  </div>
                  <dl class="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    <div>
                      <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                        {{
                          'request_form.restricted_view.device_sim_1'
                            | translate
                        }}
                      </dt>
                      <dd>{{ device.name || '-' }}</dd>
                    </div>
                    <div>
                      <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                        {{
                          'request_form.restricted_view.device_sim_2'
                            | translate
                        }}
                      </dt>
                      <dd>{{ device.secondSim || '-' }}</dd>
                    </div>
                    <div>
                      <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                        {{
                          'request_form.restricted_view.device_install_date'
                            | translate
                        }}
                      </dt>
                      <dd>
                        {{ (device.installDate | date: 'dd.MM.yyyy') || '-' }}
                      </dd>
                    </div>
                    @if (device.mountType) {
                      <div>
                        <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                          {{
                            'request_form.restricted_view.device_mount_type'
                              | translate
                          }}
                        </dt>
                        <dd>{{ device.mountType }}</dd>
                      </div>
                    }
                    @if (device.onboardVoltage) {
                      <div>
                        <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                          {{
                            'request_form.restricted_view.device_onboard_voltage'
                              | translate
                          }}
                        </dt>
                        <dd>{{ device.onboardVoltage }}</dd>
                      </div>
                    }
                    @if (device.cutoffSwitch !== null) {
                      <div>
                        <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                          {{
                            'request_form.restricted_view.device_cutoff_switch'
                              | translate
                          }}
                        </dt>
                        <dd>
                          {{
                            (device.cutoffSwitch
                              ? 'request_form.devices.cutoff_switch_options.yes'
                              : 'request_form.devices.cutoff_switch_options.no'
                            ) | translate
                          }}
                        </dd>
                      </div>
                    }
                    @if (device.hasProtectCase !== null) {
                      <div>
                        <dt class="text-[var(--tui-text-tertiary)] font-semibold">
                          {{
                            'request_form.restricted_view.device_has_protect_case'
                              | translate
                          }}
                        </dt>
                        <dd>
                          {{
                            (device.hasProtectCase
                              ? 'request_form.devices.cutoff_switch_options.yes'
                              : 'request_form.devices.cutoff_switch_options.no'
                            ) | translate
                          }}
                        </dd>
                      </div>
                    }
                  </dl>
                </li>
              }
            </ul>
          } @else {
            <p class="text-[var(--tui-text-tertiary)] font-semibold">
              {{ 'request_form.restricted_view.no_devices' | translate }}
            </p>
          }
        </article>
      </section>

      <aside class="space-y-4">
        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.delivery' | translate }}
          </h2>
          <p class="font-medium">{{ request.deliveryName || '-' }}</p>
          <p class="mt-1 text-sm text-[var(--tui-text-tertiary)]">
            {{ request.deliveryAddress || '-' }}
          </p>
          <p class="mt-3 text-sm">
            {{ request.deliveryPersonName || '-' }}
            <br />
            {{ request.deliveryPersonPhone || '-' }}
          </p>
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-3 text-lg font-semibold">
            {{ 'request_form.actual_address.actual_address' | translate }}
          </h2>
          @let actualAddress = request.actualDeliveryAddress;
          @let addressDiffers =
            actualAddress &&
            request.deliveryAddress &&
            actualAddress !== request.deliveryAddress;
          <div class="flex items-start gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="mt-0.5 h-5 w-5 shrink-0"
              [class.text-[var(--tui-status-positive)]]="actualAddress"
              [class.text-[var(--tui-status-negative)]]="!actualAddress"
            >
              <path
                fill-rule="evenodd"
                d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.003 3.5-4.697 3.5-8.08C20 5.86 16.552 2 12 2S4 5.86 4 9.267c0 3.384 1.556 6.077 3.5 8.08a19.583 19.583 0 002.682 2.282 16.975 16.975 0 001.144.742zM12 12a3 3 0 100-6 3 3 0 000 6z"
                clip-rule="evenodd"
              />
            </svg>
            <div class="min-w-0">
              <p class="break-words font-medium">
                {{ actualAddress || request.deliveryAddress || '-' }}
              </p>
              @if (addressDiffers) {
                <p class="mt-1 text-sm text-amber-500">
                  {{ 'request_form.actual_address.differs' | translate }}
                </p>
              }
            </div>
          </div>
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.files' | translate }}
          </h2>
          @if (request.files.length) {
            <div class="space-y-4">
              @for (
                group of getFilesByCategory(request.files);
                track group.categoryId
              ) {
                <div>
                  <p class="mb-2 text-sm font-medium text-[var(--tui-text-tertiary)]">
                    {{ group.categoryName || '-' }}
                  </p>
                  <div class="flex flex-wrap gap-3">
                    @for (file of group.files; track file.id) {
                      <x-file-preview
                        class="cursor-pointer"
                        [src]="file.path | filePreviewUrl"
                        [title]="getFileName(file)"
                        [fallback]="
                          fileIsImage(file.filename)
                            ? [getFileStaticUrl(file.path)]
                            : undefined
                        "
                        [fallbackIcon]="
                          fileIsImage(file.filename)
                            ? '@tui.image'
                            : '@tui.file-text'
                        "
                        [badge]="file.filename || '' | fileExtension"
                        (click)="
                          fileIsImage(file.filename)
                            ? openImagePreview(file)
                            : downloadFile(file)
                        "
                        [width]="64"
                        [height]="64"
                      />
                    }
                  </div>
                </div>
              }
            </div>
          } @else {
            <p class="text-[var(--tui-text-tertiary)]">
              {{ 'request_form.restricted_view.no_files' | translate }}
            </p>
          }
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.contact' | translate }}
          </h2>
          <p class="font-medium">{{ request.contact.fullName }}</p>
          <p class="text-sm text-[var(--tui-text-tertiary)]">
            {{ request.contact.phone }}
          </p>
        </article>

        <article class="rounded-3xl bg-[var(--tui-background-base)] p-5 shadow-sm">
          <h2 class="mb-4 text-lg font-semibold">
            {{ 'request_form.restricted_view.comments' | translate }}
          </h2>
          <form class="mb-4 space-y-3" (ngSubmit)="addComment()">
            <textarea
              class="min-h-24 w-full resize-y rounded-2xl border border-[var(--tui-border-normal)] bg-[var(--tui-background-base)] px-4 py-3 outline-none focus:border-[var(--tui-background-accent-1)]"
              [formControl]="newCommentText"
              [placeholder]="
                'request_form.restricted_view.new_comment_placeholder'
                  | translate
              "
            ></textarea>
            <button
              type="submit"
              class="w-full rounded-2xl bg-[var(--tui-background-accent-1)] px-4 py-3 font-medium text-[var(--tui-text-primary-on-accent-1)] disabled:opacity-50"
              [disabled]="!newCommentText.value.trim() || $commentLoading()"
            >
              {{ 'request_form.restricted_view.send_comment' | translate }}
            </button>
          </form>

          @if (request.comments.length) {
            <ul class="space-y-3">
              @for (comment of request.comments; track comment.id) {
                <li class="rounded-2xl bg-[var(--tui-background-base-alt)] px-4 py-3">
                  <p [innerHTML]="comment.text"></p>
                  <p class="mt-2 text-xs text-[var(--tui-text-tertiary)]">
                    {{ comment.createdBy.fullname || '-' }}
                    @if (comment.createdAt) {
                      - {{ comment.createdAt | date: 'dd.MM.yyyy HH:mm' }}
                    }
                  </p>
                </li>
              }
            </ul>
          } @else {
            <p class="text-[var(--tui-text-tertiary)]">
              {{ 'request_form.restricted_view.no_comments' | translate }}
            </p>
          }
        </article>
      </aside>
    </main>
  } @else if ($viewState() === 'not-found' || $viewState() === 'error') {
    <main
      class="mx-auto flex min-h-[calc(100vh-88px)] max-w-4xl px-4 py-6 md:px-8"
    >
      <section
        class="m-auto w-full rounded-3xl bg-[var(--tui-background-base)] px-6 py-10 text-center shadow-sm md:px-10"
      >
        <div
          class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--tui-background-base-alt)] text-[var(--tui-text-tertiary)]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-8 w-8"
          >
            <path
              fill-rule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm6.72-2.53a.75.75 0 10-1.06 1.06L10.94 13.5l-3.03 2.97a.75.75 0 101.06 1.06l3.03-2.97 3.03 2.97a.75.75 0 001.06-1.06L13.06 13.5l3.03-2.97a.75.75 0 10-1.06-1.06L12 12.44 8.97 9.47z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <h2 class="text-2xl font-semibold">
          {{
            ($viewState() === 'not-found'
              ? 'request_form.restricted_view.invalid_link_title'
              : 'request_form.restricted_view.load_error_title'
            ) | translate
          }}
        </h2>
        <p
          class="mx-auto mt-3 max-w-2xl text-sm text-[var(--tui-text-tertiary)] md:text-base"
        >
          {{
            ($viewState() === 'not-found'
              ? 'request_form.restricted_view.invalid_link_description'
              : 'request_form.restricted_view.load_error_description'
            ) | translate
          }}
        </p>
        <p class="mt-6 text-sm font-medium text-[var(--tui-text-secondary)]">
          {{
            ($viewState() === 'not-found'
              ? 'request_form.restricted_view.invalid_link_action'
              : 'request_form.restricted_view.load_error_action'
            ) | translate
          }}
        </p>
      </section>
    </main>
  }
</div>

<ng-template #imagePreviewTemplate let-observer>
  @let file = currentPreviewFile();
  <tui-preview
    [rotatable]="true"
    [zoomable]="true"
    (click)="backdropPreviewClickClose($event, observer)"
  >
    @if (file?.filename) {
      <tui-preview-title>{{ file!.filename }}</tui-preview-title>
    }
    <button
      iconStart="@tui.download"
      title="Download"
      tuiIconButton
      tuiPreviewAction
      type="button"
      (click)="downloadPreviewFile()"
    ></button>
    <button
      iconStart="@tui.x"
      title="Close"
      tuiIconButton
      tuiPreviewAction
      type="button"
      (click)="observer.complete()"
    ></button>
    @if (file?.path) {
      <img alt="preview" [src]="getFileStaticUrl(file!.path)" />
    }
  </tui-preview>
</ng-template>
